// ─────────────────────────────────────────────────────────────────────────
// structuredData.js — single source of truth for OpenbooQ's schema.org JSON-LD
// ─────────────────────────────────────────────────────────────────────────
//
// WHY THIS EXISTS (GEO / AI-visibility):
//   The core entity graph (Organization, WebSite, RoofingContractor
//   LocalBusiness, Service+Offer) is ALSO hand-mirrored as a static
//   <script type="application/ld+json"> in `index.html`. That static copy is
//   what non-JS AI crawlers (GPTBot, OAI-SearchBot, PerplexityBot, ClaudeBot,
//   CCBot) read — they generally do NOT execute JavaScript, so anything a
//   React page injects via react-helmet is invisible to them. The builders
//   here are used by the React pages (city pages, homepage FAQ) to add
//   page-specific structured data for JS-rendering crawlers (Googlebot / AI
//   Overviews / AI Mode, which DO render).
//
//   KEEP EVERY FACT HONEST AND CONSISTENT with the Google Business Profile
//   and on-page copy. Google and generative engines DISCOUNT structured data
//   that contradicts on-page / GBP / review signals — contradiction makes them
//   MORE conservative about citing the brand. Accuracy is a citation asset.
//
//   If you change the values below, mirror the change in index.html.
// ─────────────────────────────────────────────────────────────────────────

export const SITE_URL = 'https://openbooq.com';
export const ORG_ID = `${SITE_URL}/#organization`;
export const LOCALBUSINESS_ID = `${SITE_URL}/#localbusiness`;
export const WEBSITE_ID = `${SITE_URL}/#website`;
export const SERVICE_ID = `${SITE_URL}/#roof-replacement`;
export const OFFER_ID = `${SITE_URL}/#roof-offer`;

// First-party brand logo (replaces the previous external img.rocket.new URL so
// the entity image is stable and served from our own domain).
export const LOGO_URL = `${SITE_URL}/assets/images/Openbooq_blue-1773069231613.png`;
export const HERO_IMAGE_URL = `${SITE_URL}/assets/images/OpenbooQ1-1767636399707.png`;

// NAP — must match the Google Business Profile exactly.
export const TELEPHONE = '+1-937-910-8791';
export const EMAIL = 'connect@openbooq.com';
export const POSTAL_ADDRESS = {
  '@type': 'PostalAddress',
  streetAddress: '1 E 4th St',
  addressLocality: 'Dayton',
  addressRegion: 'OH',
  postalCode: '45402',
  addressCountry: 'US',
};

export const SAME_AS = [
  'https://www.facebook.com/openbooq',
  'https://www.instagram.com/openbooq',
  'https://www.linkedin.com/company/openbooq',
  'https://www.tiktok.com/@openbooq',
  // TODO(entity-runbook): uncomment each as the team claims/stands up the
  // profile (see the AEO entity runbook). Mirror any addition into
  // index.html's static @graph AND scripts/geo/markets.data.mjs COMPANY.sameAs.
  // 'https://www.google.com/maps?cid=<GBP_CID>',                    // Google Business Profile (claim → get CID)
  // 'https://www.yelp.com/biz/<openbooq-dayton-slug>',              // Yelp (claim page)
  // 'https://www.bbb.org/us/oh/dayton/profile/<category>/<slug>',   // BBB (accreditation profile)
  // 'https://www.wikidata.org/wiki/<QID>',                          // Wikidata (after item is created)
];

// GAF — OpenbooQ's exclusive manufacturer partner. OpenbooQ installs GAF
// products exclusively and its crews are GAF-certified. This is a top-tier
// manufacturer-authority signal; surface it in the entity graph. (Reused by
// index.html's static @graph — keep both in sync.)
export const GAF_BRAND = { '@type': 'Brand', name: 'GAF' };
export const GAF_AFFILIATION = { '@type': 'Organization', name: 'GAF', url: 'https://www.gaf.com' };
export const GAF_CREDENTIAL = {
  '@type': 'EducationalOccupationalCredential',
  credentialCategory: 'certification',
  name: 'GAF-certified roofing installer',
  recognizedBy: { '@type': 'Organization', name: 'GAF', url: 'https://www.gaf.com' },
};

// Last-known live Google aggregate. Keep in sync with the reviews page
// FALLBACK constants and the live GBP. Honest numbers only.
export const AGGREGATE_RATING = {
  '@type': 'AggregateRating',
  ratingValue: '5.0',
  reviewCount: '32',
  bestRating: '5',
  worstRating: '1',
};

// The live retail markets — each has a dedicated city page.
// Dallas is the Texas launch market (licensed statewide in TX, crews ready).
export const LIVE_CITIES = [
  { name: 'Cincinnati', region: 'OH', slug: 'cincinnati' },
  { name: 'Columbus', region: 'OH', slug: 'columbus' },
  { name: 'Dayton', region: 'OH', slug: 'dayton' },
  { name: 'Dallas', region: 'TX', slug: 'dallas' },
];

export const AREA_SERVED = LIVE_CITIES.map((c) => ({
  '@type': 'City',
  name: `${c.name}, ${c.region}`,
}));

// The differentiated commercial terms — the concrete, citable facts AI engines
// love to quote. Reused by the national Service and every city Service.
export const ROOF_OFFER = {
  '@type': 'Offer',
  '@id': OFFER_ID,
  name: 'Transparent online roof replacement pricing',
  description:
    'Roofing materials sold at wholesale cost plus one transparent Service & Warranty fee of about 20% — versus the 30–50% margin typical of traditional roofers. No deposit: $0 at signing, $0 during the work, and the homeowner pays nothing until the new roof is installed and passes final inspection. Monthly financing is available through Enhancify.',
  priceCurrency: 'USD',
  availability: 'https://schema.org/InStock',
  areaServed: AREA_SERVED,
  seller: { '@id': ORG_ID },
  eligibleRegion: { '@type': 'Country', name: 'United States' },
};

// Licensed footprint — Ohio and Texas only, with crews statewide in both.
export const STATES_SERVED = [
  { '@type': 'State', name: 'Ohio' },
  { '@type': 'State', name: 'Texas' },
];

const ORG_DESCRIPTION =
  'OpenbooQ is a direct-to-consumer online roofing company — "the wholesale roof company" — and an official GAF partner that installs premium GAF products, with GAF-certified crews. Homeowners enter their address, get an exact, transparent roof-replacement price in about 30 minutes (no salesperson, no in-home visit, no callback), and book installation by vetted local crews. GAF materials are sold at cost plus a transparent ~20% Service & Warranty fee — that is the fee\'s only name — and OpenbooQ takes no deposit: $0 at signing, $0 during the work, and you pay nothing until the roof is installed and passes final inspection. Licensed in Ohio and Texas. Retail only: OpenbooQ sells full roof replacement, does not sell roof repair, and does not file, negotiate, adjust, or manage insurance claims — it is not a public adjuster or an insurance representative. OpenbooQ\'s only published phone number is (937) 910-8791.';

// ─── Core entity nodes (kept identical to the static graph in index.html) ───

export const organizationNode = {
  '@type': 'Organization',
  '@id': ORG_ID,
  name: 'OpenbooQ',
  legalName: 'OpenbooQ Inc.',
  url: SITE_URL,
  logo: { '@type': 'ImageObject', url: LOGO_URL },
  image: LOGO_URL,
  slogan: 'The wholesale roof company.',
  description: ORG_DESCRIPTION,
  foundingDate: '2024',
  areaServed: STATES_SERVED,
  knowsAbout: [
    'Roof replacement',
    'GAF roofing systems',
    'GAF Timberline HDZ, UHDZ and AS II shingles',
    'Transparent roofing pricing',
    'Roof financing',
  ],
  brand: GAF_BRAND,
  affiliation: GAF_AFFILIATION,
  hasCredential: GAF_CREDENTIAL,
  sameAs: SAME_AS,
  // Full canonical NAP on the Organization node too (not only LocalBusiness) —
  // AI engines reconcile the org entity against GBP/Bing/Apple listings.
  address: POSTAL_ADDRESS,
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: TELEPHONE,
    email: EMAIL,
    contactType: 'customer service',
    availableLanguage: 'English',
    areaServed: 'US',
  },
};

export const websiteNode = {
  '@type': 'WebSite',
  '@id': WEBSITE_ID,
  url: SITE_URL,
  name: 'OpenbooQ',
  description:
    'Buy your roof online with transparent pricing — an exact quote in about 30 minutes. No in-home sales visits, no hidden markups.',
  publisher: { '@id': ORG_ID },
  inLanguage: 'en-US',
};

export const localBusinessNode = {
  '@type': ['HomeAndConstructionBusiness', 'RoofingContractor'],
  '@id': LOCALBUSINESS_ID,
  name: 'OpenbooQ',
  url: SITE_URL,
  telephone: TELEPHONE,
  email: EMAIL,
  logo: LOGO_URL,
  image: LOGO_URL,
  description:
    'Online roofing company offering transparent roof-replacement pricing with an exact quote in about 30 minutes, premium GAF products (installed by GAF-certified crews), and vetted local installers — with no deposit (pay after final inspection) and no in-home sales visits. Retail only: full roof replacement, no roof repairs, no insurance claim handling.',
  brand: GAF_BRAND,
  hasCredential: GAF_CREDENTIAL,
  slogan: 'The wholesale roof company.',
  address: POSTAL_ADDRESS,
  geo: { '@type': 'GeoCoordinates', latitude: 39.7589, longitude: -84.1916 },
  priceRange: '$$',
  paymentAccepted: 'Cash, Credit Card, ACH, Financing (Enhancify)',
  currenciesAccepted: 'USD',
  areaServed: AREA_SERVED,
  parentOrganization: { '@id': ORG_ID },
  makesOffer: { '@id': OFFER_ID },
  aggregateRating: AGGREGATE_RATING,
  sameAs: SAME_AS,
};

export const serviceNode = {
  '@type': 'Service',
  '@id': SERVICE_ID,
  serviceType: 'Roof replacement',
  name: 'Online roof replacement',
  description:
    'Full roof replacement priced online with an exact quote in about 30 minutes — materials at wholesale cost plus one transparent ~20% Service & Warranty fee, installed by vetted local crews, with no deposit: nothing is due until final inspection passes. Full replacement only — OpenbooQ does not sell roof repair and does not handle insurance claims.',
  provider: { '@id': ORG_ID },
  areaServed: AREA_SERVED,
  offers: ROOF_OFFER,
};

// The full graph, in the order used by index.html. Exported for reference/tests;
// the React app does NOT inject this (index.html owns the static copy).
export const CORE_GRAPH = {
  '@context': 'https://schema.org',
  '@graph': [organizationNode, websiteNode, localBusinessNode, serviceNode],
};

// ─── Builders (page-specific structured data, injected via react-helmet) ────

export function buildBreadcrumb(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: it.item,
    })),
  };
}

export function buildFaqPage(faqs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };
}

// A city page describes the org PROVIDING roof replacement in that area — modeled
// as a Service with areaServed = the city (an honest service-area representation;
// OpenbooQ has one HQ, not a storefront per city). Reuses the national Offer terms.
// `description` / `offer` may be overridden per city so newer pages can carry the
// current messaging canon ("Service & Warranty fee", GAF-exclusive)
// without silently rewording the older Ohio pages' markup.
export function buildCityService({ name, region, url, description, offer }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${url}#service`,
    serviceType: 'Roof replacement',
    name: `Roof Replacement in ${name}, ${region}`,
    description:
      description ||
      `Transparent online roof-replacement pricing for ${name}, ${region} homeowners — premium GAF, Owens Corning, Atlas and TAMKO shingles installed by vetted local crews, materials at wholesale cost plus a transparent ~20% Service & Warranty fee, and 0% due upfront until the roof passes final inspection.`,
    provider: { '@id': ORG_ID },
    areaServed: { '@type': 'City', name: `${name}, ${region}` },
    offers: offer || ROOF_OFFER,
    url,
  };
}

// ─── Homepage FAQ (plain-text mirror of the visible homepage FAQSection) ────
// These match the on-page Q&A so the FAQPage markup is backed by visible content.
export const HOMEPAGE_FAQS = [
  {
    question: 'How much does OpenbooQ charge — and why is it cheaper than a local roofer?',
    answer:
      'OpenbooQ sells roofing materials at wholesale cost and adds one transparent Service & Warranty fee of about 20%, versus the 30–50% margin typical of traditional roofers. There are no commissioned salespeople and no material markup, which is where the 30–50% savings comes from. You see the itemized price online before anyone calls you.',
  },
  {
    question: 'Do I really pay $0 down for a new roof?',
    answer:
      'Yes. There is 0% due upfront. You pay nothing until your new roof is installed and you have walked and approved the finished work at final inspection. Because OpenbooQ pays for your materials, schedules the crew, and pulls permits before any money moves, the risk sits on our side, not yours.',
  },
  {
    question: 'What happens after I enter my address?',
    answer:
      'You get a line-itemed roof-replacement quote texted and emailed to you in about 30 minutes. There is no in-home inspection and no sales pitch. One OpenbooQ specialist may follow up by phone to make sure the quote makes sense — take the call or skip it.',
  },
  {
    question: 'Are these the same materials a local roofer would install?',
    answer:
      'Yes. OpenbooQ installs premium shingles from GAF (Timberline HDZ), Owens Corning (Oakridge, Duration), Atlas (Pinnacle Pristine) and TAMKO (Titan XT) — the top U.S. residential brands — sourced from the same regional distributors local roofers use. The difference is the price: wholesale cost with no markup.',
  },
  {
    question: 'Who actually installs the roof?',
    answer:
      'Vetted, manufacturer-certified local install crews managed by OpenbooQ, with an assigned project manager on-site or on-call from tear-off through cleanup and body cameras worn for the entire job. Work is not subcontracted to a random crew.',
  },
  {
    question: 'Can I finance my roof, and can I cancel after signing?',
    answer:
      'Monthly financing is available through Enhancify, and you can also pay upfront or through escrow. You can cancel free of charge within 72 hours of signing; after that a 25% cancellation fee applies because materials, crew and permits are already committed to your job.',
  },
  {
    question: 'Which cities does OpenbooQ serve?',
    answer:
      'OpenbooQ is live in Cincinnati, Columbus and Dayton, Ohio, and is now live in Dallas, Texas — its first Texas market, with crews statewide. Enter your address to confirm coverage and get your price.',
  },
];
